/**
 * DoorBird + BAS-IP IP intercom catalog — both brands are premium
 * SIP 2.0-based IP intercom systems.
 *
 * All specs verified against doorbird.com/en/shop and bas-ip.com
 * product pages on 2026-05-15. URL cited per entry.
 *
 * Schema normalisation:
 *   - source `side: 'indoor-station'` → `'indoor-monitor'`
 *   - source `screenInches` → `monitorInches`
 *   - source `keypad: true` / `fingerprint: true` flags collapsed into
 *     the schema's `accessControl` array (values 'keypad' / 'fingerprint')
 *   - extra fields not in schema (`wifi`, `touchscreen`, `android`,
 *     `ikRating`) dropped — leaving them in tagline if relevant.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DOORBIRD_BASIP_INTERCOM_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // DoorBird — outdoor stations (D11xx fixed, D21xx modular)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'doorbird-d1101v',
    manufacturer: 'DoorBird',
    model: 'D1101V',
    category: 'intercom',
    tagline: '1-button Full HD IP-SIP video door station with 180° lens and night vision',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423866744',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP54',
      poe: true,
    },
  },

  {
    id: 'doorbird-d1102v',
    manufacturer: 'DoorBird',
    model: 'D1102V',
    category: 'intercom',
    tagline: '2-button Full HD IP-SIP video door station for multi-tenant buildings',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423866768',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 2,
      poe: true,
    },
  },

  {
    id: 'doorbird-d1101kv',
    manufacturer: 'DoorBird',
    model: 'D1101KV',
    category: 'intercom',
    tagline: '1-button Full HD IP-SIP door station with keypad (up to 500 PIN codes)',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423871601',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      poe: true,
      accessControl: ['keypad'],
    },
  },

  {
    id: 'doorbird-d1101fv',
    manufacturer: 'DoorBird',
    model: 'D1101FV',
    category: 'intercom',
    tagline: '1-button Full HD IP-SIP door station with 50-fingerprint reader',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423872141',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      poe: true,
      accessControl: ['fingerprint'],
    },
  },

  {
    id: 'doorbird-d1101kh',
    manufacturer: 'DoorBird',
    model: 'D1101KH Modern',
    category: 'intercom',
    tagline: '1-button Full HD IP-SIP door station with keypad, Hahn-compatible mount',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423867574',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      poe: true,
      accessControl: ['keypad'],
    },
  },

  {
    id: 'doorbird-d2101v',
    manufacturer: 'DoorBird',
    model: 'D2101V',
    category: 'intercom',
    tagline: 'Modular 1-button HD IP-SIP door station with 180° hemispheric lens',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423870055',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      poe: true,
    },
  },

  {
    id: 'doorbird-d2101kv',
    manufacturer: 'DoorBird',
    model: 'D2101KV',
    category: 'intercom',
    tagline: 'Modular 1-button HD IP-SIP door station with keypad module',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423869998',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      poe: true,
      accessControl: ['keypad'],
    },
  },

  {
    id: 'doorbird-d2101fv',
    manufacturer: 'DoorBird',
    model: 'D2101FV',
    category: 'intercom',
    tagline: 'Modular 1-button HD IP-SIP door station with 50-fingerprint reader',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423872479',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      poe: true,
      accessControl: ['fingerprint'],
    },
  },

  {
    id: 'doorbird-d2102v',
    manufacturer: 'DoorBird',
    model: 'D2102V',
    category: 'intercom',
    tagline: 'Modular 2-button HD IP-SIP door station for multi-tenant buildings',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423870666',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 2,
      poe: true,
    },
  },

  // DoorBird — indoor stations
  {
    id: 'doorbird-a1101',
    manufacturer: 'DoorBird',
    model: 'A1101',
    category: 'intercom',
    tagline: '4-inch touch IP-SIP indoor station with two-way HD audio',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4260423872677',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 4,
      poe: true,
    },
  },

  {
    id: 'doorbird-a1104t',
    manufacturer: 'DoorBird',
    model: 'A1104T',
    category: 'intercom',
    tagline: '7-inch IPS touch IP-SIP indoor station for residential and commercial use',
    verified: true,
    datasheetUrl: 'https://www.doorbird.com/en/shop/?ean=4251489614073',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // BAS-IP — outdoor stations (AA/AV/BI series)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'basip-aa-07fbi',
    manufacturer: 'BAS-IP',
    model: 'AA-07FBI',
    category: 'intercom',
    tagline: 'Multi-apartment IP-SIP entrance panel with induction-loop output',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/aa-07fbi/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'basip-aa-14fbis',
    manufacturer: 'BAS-IP',
    model: 'AA-14FBIS',
    category: 'intercom',
    tagline: 'Luxury IP-SIP multi-apartment panel with face recognition and multi-factor auth',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/bas-ip-aa-14fbis/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'basip-av-06m',
    manufacturer: 'BAS-IP',
    model: 'AV-06M',
    category: 'intercom',
    tagline: 'Compact single-button IP-SIP entrance panel',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/av-06m/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'basip-av-08fbil',
    manufacturer: 'BAS-IP',
    model: 'AV-08FBIL',
    category: 'intercom',
    tagline: '1-button IP-SIP panel with induction-loop output and status indicator',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/av-08fbil/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'basip-av-09kfbi',
    manufacturer: 'BAS-IP',
    model: 'AV-09KFBI',
    category: 'intercom',
    tagline: 'IP-SIP entry panel with backlit keypad, face recognition and multi-factor auth',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/av-09kfbi/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['keypad'],
    },
  },

  {
    id: 'basip-bi-08fbi',
    manufacturer: 'BAS-IP',
    model: 'BI-08FBI',
    category: 'intercom',
    tagline: '8-button IP-SIP entrance panel with induction-loop output (12 V DC powered)',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/bi-08fbi/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 8,
      ipRating: 'IP65',
      poe: false,
    },
  },

  {
    id: 'basip-bi-12fbi',
    manufacturer: 'BAS-IP',
    model: 'BI-12FBI',
    category: 'intercom',
    tagline: '12-button IP-SIP entrance panel with induction-loop output',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/bi-12fbi/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 12,
      ipRating: 'IP65',
      poe: false,
    },
  },

  // BAS-IP — indoor monitors
  {
    id: 'basip-at-10',
    manufacturer: 'BAS-IP',
    model: 'AT-10',
    category: 'intercom',
    tagline: '10-inch Android 10 IP-SIP indoor video phone with 1280×800 IPS touch display',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/at-10/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 10,
      poe: true,
    },
  },

  {
    id: 'basip-at-07',
    manufacturer: 'BAS-IP',
    model: 'AT-07',
    category: 'intercom',
    tagline: '7-inch Android 10 IP-SIP indoor video phone with IPS touch display',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/at-07/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  {
    id: 'basip-az-07lf',
    manufacturer: 'BAS-IP',
    model: 'AZ-07LF',
    category: 'intercom',
    tagline: '7-inch IP-SIP indoor monitor with Wi-Fi 6 and horizontal/vertical mounting',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/bas-ip-az-07lf/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  {
    id: 'basip-au-04laf',
    manufacturer: 'BAS-IP',
    model: 'AU-04LAF',
    category: 'intercom',
    tagline: '4.3-inch IP-SIP indoor video phone with Wi-Fi',
    verified: true,
    datasheetUrl: 'https://bas-ip.com/hardware/au-04laf/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 4.3,
      poe: true,
    },
  },
];
